// The RFQ Center's rows: every RFQ with who is waiting on whom, the latest
// supplier message, the quote in hand and the suggested next moves.
// apps/smart-catalog/frontend/src/services/rfqCenterService.ts

import apiClient from './api';

export type CenterState = 'you' | 'wait' | 'quoted' | 'draft' | 'closed';

export interface CenterSuggestion {
  id: string;
  label: string;
  kind: 'reply' | 'review-draft' | 'accept-quote';
  best?: boolean;
  why?: string;
  text?: string;
  quoteId?: string;
}

export interface CenterLine {
  partId?: string;
  partNumber?: string;
  mpn?: string;
  maker?: string;
  description?: string;
  quantity?: number;
  unit?: string;
  targetPrice?: number;
  targetCurrency?: string;
}

export interface CenterRow {
  /** Written by the AI autopilot. */
  pilotRequestId?: string;
  rfqId: string;
  rfqNumber: string;
  rfqStatus: string;
  rfqType?: string;
  threadId?: string;
  bomId?: string;
  supplier: { id: string; name: string };
  lines: CenterLine[];
  sentAt?: string;
  createdAt?: string;
  state: CenterState;
  stale: boolean;
  chip: string;
  waitingOn: { who: 'us' | 'supplier' | 'nobody'; since?: string; days?: number; detail?: string };
  nowWith?: { name: string; email?: string; isNew: boolean };
  quote?: { quoteId?: string; unitPrice?: number; currency?: string; leadTimeDays?: number; moq?: number; reviewStatus?: string; count: number };
  latest?: { messageId?: string; who: string; when?: string; text: string };
  supplierMessages: number;
  overview: { points: string[]; byAgent: boolean };
  suggestions: CenterSuggestion[];
  /** A seller that came to us through another seller's RFQ: who referred it. */
  referredBy?: string;
}

/** A seller we did not write to that wrote about one of our RFQs: the buyer says who it is. */
export interface CenterReferral {
  mailId: string;
  domain: string;
  rfqId?: string;
  rfqNumber?: string;
  referrerName?: string;
  item: string;
  mails: number;
  lastAt?: string;
  latest: { name?: string; email: string; subject?: string; text: string };
  /** Why its mail looks sent by a tool, when it does. */
  automatic?: string[];
}

export type ReferralChoice = 'new-seller' | 'same-seller' | 'not-ours';

export interface CenterCounts {
  all: number;
  you: number;
  wait: number;
  stale: number;
  quoted: number;
  draft: number;
}

export interface CenterData {
  rows: CenterRow[];
  counts: CenterCounts;
  truncated: boolean;
  referrals?: CenterReferral[];
}

export interface TimelineEntry {
  messageId?: string;
  kind: 'supplier' | 'sent' | 'automatic';
  who: string;
  when: string;
  snippet: string;
  attachments: number;
}

const rfqCenterService = {
  /** The buyer's answer about a referred seller; it covers every mail of that seller on the RFQ. */
  async resolveReferral(mailId: string, body: { choice: ReferralChoice; rfqNumber?: string; sellerName?: string }): Promise<{ outcome: ReferralChoice; seller?: { rfqId: string; supplierName: string }; mails: number }> {
    const response = await apiClient.post(`/buyer-agent/referrals/${encodeURIComponent(mailId)}/resolve`, body);
    return response.data?.data;
  },

  async getRows(bomId?: string | null): Promise<CenterData> {
    const response = await apiClient.get('/buyer-agent/rfq-center', { params: bomId ? { bomId } : undefined });
    return response.data?.data;
  },

  /** One RFQ's conversation as short lines, oldest first. */
  async getTimeline(rfqId: string): Promise<TimelineEntry[]> {
    const response = await apiClient.get(`/buyer-agent/rfq-center/${rfqId}/timeline`);
    return response.data?.data ?? [];
  },
};

export default rfqCenterService;
